import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import Tooltip from '@mui/material/Tooltip';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { distanceMetricToImperial, isCustomUnitField, volumeMetricToImperial } from 'utils/unitConversions';
import {
  compareStoredVolume,
  formatVolumeDelta,
  formatVolumeDeltaPercent
} from 'modules/project/StopeFillPage/Three/pourVolumeDelta';
import { PIPE_COLOURS } from '../../MineModel/GConstants';
import { StopeDimensions } from './stopeFillSceneState';

export interface PourVolumeData {
  entityId: string;
  name: string;
  /** The volume STORED on the pour - what the rest of the app plans and reports against. */
  volume: number;
  /** What the current stope shape says this slice holds. Undefined before the fill walk has run. */
  recomputedVolume?: number;
  /** The beach slope this pour was planned at: its own stored angle, or 1.25 if it predates the field. */
  beachAngleDegrees?: number;
  height?: number;
  riseRate?: number;
}

interface StopeVolumeTableProps {
  totalVolume: number;
  remainingVolume: number;
  pours: PourVolumeData[];
  dimensions?: StopeDimensions;
  unitSystemPreference?: UnitSystem;
  customUnitFields?: CustomUnitFields[];
}

const formatRiseRate = (riseRate: number | undefined): string => (
  riseRate && Number.isFinite(riseRate) ? riseRate.toFixed(2) : '—'
);

const formatHeight = (height: number | undefined): string => (
  height && Number.isFinite(height) ? height.toFixed(1) : '—'
);

// Trailing zeros are dropped so the common 1.25 and 2.5 read cleanly rather than as 1.250 / 2.500.
const formatAngle = (angle: number | undefined): string => (
  angle !== undefined && Number.isFinite(angle) ? `${Number(angle.toFixed(2))}` : '—'
);

// The stored volume is what the rest of the app plans against, so it stays the headline figure. When the
// recomputed figure disagrees materially the difference is shown underneath in warning colour - that is
// the only signal an engineer gets that this pour predates the geometry fix and needs "Update Volumes".
// Anything below the threshold stays invisible so the panel is not permanently noisy.
const renderVolumeDelta = (pour: PourVolumeData, toVolume: (value: number) => number): JSX.Element | null => {
  const comparison = compareStoredVolume(pour.volume, pour.recomputedVolume);
  if (!comparison || !comparison.isMaterial) return null;

  const percent = formatVolumeDeltaPercent(comparison.relative);
  const title = i18n._(
    'Recomputed against the current stope shape: {recomputed}. Use "Update Volumes" to apply.',
    { recomputed: Math.round(toVolume(pour.recomputedVolume!)) },
  );

  return (
    <Tooltip title={title}>
      <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1.2 }}>
        {formatVolumeDelta(toVolume(comparison.difference))}
        {percent ? ` (${percent})` : ''}
      </Typography>
    </Tooltip>
  );
};

const StopeVolumeTable = ({
  totalVolume, remainingVolume, pours, dimensions, unitSystemPreference, customUnitFields,
}: StopeVolumeTableProps) => {
  // Incoming figures are canonical metric; convert per the project's unit gates (the same fields
  // the stope/pour notes use: PourVolume for volumes, PipeLength for heights and rise rates).
  const isImperialVolume = unitSystemPreference !== undefined
    && isCustomUnitField(unitSystemPreference, customUnitFields ?? [], unitLabels[BasicDisplayUnits.Volume].customUnitField);
  const isImperialLength = unitSystemPreference !== undefined
    && isCustomUnitField(unitSystemPreference, customUnitFields ?? [], unitLabels[BasicDisplayUnits.Distance].customUnitField);
  const toVolume = (value: number) => (isImperialVolume ? volumeMetricToImperial(value) : value);
  const toLength = (value?: number) => (value !== undefined && isImperialLength ? distanceMetricToImperial(value) : value);
  const formatDimension = (value: number) => toLength(value)!.toFixed(1);

  const filledVolume = totalVolume - remainingVolume;
  const filledPercent = totalVolume > 0 ? (filledVolume / totalVolume) * 100 : 0;

  return (
    <>
      {dimensions && (
        <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            <Trans id="Dimensions (L × W × H)">Dimensions (L × W × H)</Trans>
            {`: ${formatDimension(dimensions.length)} × ${formatDimension(dimensions.width)} × ${formatDimension(dimensions.height)} ${isImperialLength ? 'ft' : 'm'}`}
          </Typography>
        </Box>
      )}
      {/* Fixed layout: with automatic layout the five columns grow to their content and push the
          table wider than the panel, which shows a horizontal scrollbar. Here the numeric columns
          get a set width and the name column takes whatever is left, truncating with an ellipsis. */}
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { py: 0.5, px: 0.5, border: 0, overflow: 'hidden' },
          '& .MuiTableCell-root:first-of-type': { pl: 1 },
          '& .MuiTableCell-root:last-of-type': { pr: 1 },
        }}
      >
        <colgroup>
          <col />
          <col style={{ width: 44 }} />
          <col style={{ width: 62 }} />
          <col style={{ width: 52 }} />
          <col style={{ width: 56 }} />
        </colgroup>
        <TableBody>
          <TableRow>
            <TableCell />
            <TableCell align="right">
              <Typography variant="caption" color="text.secondary">
                {isImperialLength ? <Trans id="Ht (ft)">Ht (ft)</Trans> : <Trans id="Ht (m)">Ht (m)</Trans>}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" color="text.secondary">
                {isImperialVolume ? <Trans id="Vol (ft³)">Vol (ft³)</Trans> : <Trans id="Vol (m³)">Vol (m³)</Trans>}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Tooltip title={i18n._('Paste beach angle, in degrees from horizontal')}>
                <Typography variant="caption" color="text.secondary">
                  <Trans id="Beach (°)">Beach (°)</Trans>
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" color="text.secondary">
                {isImperialLength ? <Trans id="Rise (ft/h)">Rise (ft/h)</Trans> : <Trans id="Rise (m/h)">Rise (m/h)</Trans>}
              </Typography>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="body2" fontWeight="medium">
                <Trans id="Total">Total</Trans>
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <Typography variant="body2">
                {Math.round(toVolume(totalVolume))}
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>

          {pours.map((pour, index) => (
            <TableRow key={pour.entityId}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: PIPE_COLOURS[index % PIPE_COLOURS.length],
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" noWrap title={pour.name}>
                    {pour.name || `Pour ${index + 1}`}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {formatHeight(toLength(pour.height))}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {Math.round(toVolume(pour.volume))}
                </Typography>
                {renderVolumeDelta(pour, toVolume)}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {formatAngle(pour.beachAngleDegrees)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary">
                  {formatRiseRate(toLength(pour.riseRate))}
                </Typography>
              </TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell colSpan={5} sx={{ borderTop: 1, borderColor: 'divider' }} />
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="body2" fontWeight="medium">
                <Trans id="Remaining">Remaining</Trans>
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <Typography variant="body2">
                {Math.round(toVolume(remainingVolume))}
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                <Trans id="Filled">Filled</Trans>
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <Typography variant="body2" color="text.secondary">
                {Math.round(filledPercent)}
                %
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default StopeVolumeTable;
